import { Injectable } from '@angular/core';
import {
  MemoizedSelector,
  select,
  Store,
  ActionCreator,
  ActionsSubject,
  createAction,
  props,
} from '@ngrx/store';
import { DefaultProjectorFn } from '@ngrx/store/src/selector';
import { Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { TemporaryState } from 'app/store/temporary/reducers';
import { MwDataMapService } from './data-map.service';
import { MwSelectorModel } from 'app/models/selector.model';
import { MwActiveFilterModel } from 'app/models/active-filter.model';
import { ofType } from '@ngrx/effects';
import { TypedAction } from '@ngrx/store/src/models';

@Injectable()
export class MwNgrxUtilsService {
  constructor(
    private store: Store<TemporaryState>,
    private mappingService: MwDataMapService,
    private actionsSubject: ActionsSubject
  ) {}

  subscriptionToSelector<T>(
    unsubscribeTarget: Subject<any>,
    selector: MemoizedSelector<object, T[], DefaultProjectorFn<T[]>>,
    idSelector: (item: T) => number,
    valueSelector: (item: T) => string,
    resultCallback: (result: MwSelectorModel[]) => any,
    isActiveSelector: (item: T) => boolean = () => undefined
  ): void {
    this.store
      .pipe(takeUntil(unsubscribeTarget))
      .pipe(select(selector))
      .subscribe((result) => {
        resultCallback(
          this.mappingService.mapToSelectorModel(
            result,
            idSelector,
            valueSelector,
            isActiveSelector
          )
        );
      });
  }

  subscriptionToFilterModel<T>(
    unsubscribeTarget: Subject<any>,
    selector: MemoizedSelector<object, T[], DefaultProjectorFn<T[]>>,
    idSelector: (item: T) => number,
    valueSelector: (item: T) => string,
    resultCallback: (result: MwActiveFilterModel) => any,
    isActiveSelector: (item: T) => boolean
  ): void {
    this.subscriptionToSelector(
      unsubscribeTarget,
      selector,
      idSelector,
      valueSelector,
      (result) => resultCallback(new MwActiveFilterModel(result)),
      isActiveSelector
    );
  }

  actionResponse<P extends object, P2 extends object, P3 extends object>(
    actions: {
      action: ActionCreator<string, (props: P) => P & TypedAction<string>>;
      success: ActionCreator<string, (props: P2) => P2 & TypedAction<string>>;
      failure: ActionCreator<string, (props: P3) => P3 & TypedAction<string>>;
    },
    payload?: any
  ): Promise<{
    action?: P2 & TypedAction<string>;
    error?: P3 & TypedAction<string>;
  }> {
    return new Promise((resolve, reject) => {
      this.store.dispatch(
        createAction(actions.action.type, props<{ payload }>())(payload)
      );
      this.actionsSubject
        .pipe(ofType(actions.success.type, actions.failure.type), take(1))
        .subscribe((action: any) => {
          if (action.type === actions.success.type) {
            resolve({ action });
          } else {
            reject({ error: action.error.errors[0].errorDescription });
          }
        });
    });
  }
}
